/**
 * Beat 3 in solo (screens §P6 solo variant; `decideSlots.soloReview`): the CRO memo for the current draft version grouped by severity,
 * showing the memo's content ("Read the note") marks it read on the server (`soloOpenCroMemo`, `croMemo.openedAt`: it survives a reload) and turns on the one "Submit and run the year": it records the acknowledgement (`soloAckCroMemo`, with the note) and submits (`soloAdvance {submit}`); an objection is signed over in the same one action (the reason is the acknowledgement). No CRO person, no countdown. A stale memo (`CRO_MEMO_STALE`) is rebuilt and needs a fresh acknowledgement.
 */
import { useSoloAdvance } from "./useSoloAdvance";
import { Icon } from "../theme/icons";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState, type ComponentType, type ReactNode } from "react";
import { Drawer } from "../theme/Drawer";
import { GxStickyBar } from "../theme/GxStickyBar";
import { useBand } from "../theme/breakpoints";
import type { ErrorBody, TeamState } from "@bsim/contracts";
import type { SoloReviewProps } from "../decisions/slotTypes";
import { navigate } from "../shell/history";
import { GxCroMemoList, memoReceipt } from "../theme/CroMemoList";
import { GxCard, GxErrorBanner } from "../theme/primitives";
import { GxPill } from "../theme/GxPill";
import { OVERRIDE_MIN } from "../signoff/SignPanel";
import { GxLimitedText, INPUT_LIMITS } from "../theme/GxLimitedText";
import { standingPolicyLine } from "../decisions/reviewWords";
import { ReviewPending, MEMO_PENDING_NOTE } from "../decisions/review/ReviewCards";
import "./solo.css";
import "../decisions/review/review.css";

type Memo = NonNullable<TeamState["croMemo"]>;
const CHANGED_LINE = "The note changed with your plan; open it again.";
/** The same note: a reply about one memo version is applied only to that version (a same-id rebuild with other content is a different note). */
const sameNote = (a: Memo | null, b: Memo): boolean => a !== null && a.memoId === b.memoId && a.draftVersion === b.draftVersion && a.memo.memoHash === b.memo.memoHash;
const STALE_RETRIES = 5;
/** A server fault is tried once more before the card: the first memo of a round can meet an engine still warming, and the stored memo makes the second ask the cheaper one. */
const FAULT_RETRIES = 1;
const STALE_RETRY_MS = 1000;

/** The page's header strip: the round line, the title, the review counts and the submit area (the same strip while the memo is still being built, so nothing moves when it arrives). */
function ReviewHead({ eyebrow, totals, children }: { readonly eyebrow: string | undefined; readonly totals: SoloReviewProps["totals"]; readonly children: ReactNode }) {
  return (
    <header className="gx-rv-head">
      <div>
        {eyebrow === undefined ? null : <div className="gx-eyebrow gx-num">{eyebrow}</div>}
        <h1 className="gx-h1">Review &amp; submit</h1>
      </div>
      <div className="gx-rv-acts">
        {totals === undefined ? null : <span className="gx-rv-count" data-testid="review.totals"><span data-testid="review.totals.line"><b>{totals.adjusted} adjusted</b> {"\u00b7"} {totals.maintained} maintained {"\u00b7"} {totals.toReview} to review</span></span>}
        {totals === undefined || totals.toReview === 0 ? null : <span className="gx-small gx-muted" data-testid="decide.sign.standing">{standingPolicyLine(totals.toReview)}</span>}
        {children}
      </div>
    </header>
  );
}

export const SoloReview: ComponentType<SoloReviewProps> = ({ team, soloCroMemo, soloOpenCroMemo, soloAckCroMemo, soloAdvance, onMemo, eyebrow, totals, plan, risk, riskPending, reasons }) => {
  // On a phone the submit lives in a sticky bar at the foot (MOBILE-ARCHITECTURE §4.4): its lead says how many things stand before it and why it is locked, and "Read the note" is the primary action while it is.
  const phone = useBand() === "phone";
  const round = team.round;
  const draftVersion = team.draft?.version ?? null;
  const runId = team.cohort.id;
  const [memo, setMemo] = useState<Memo | null>(team.croMemo?.draftVersion === draftVersion ? team.croMemo : null);
  const [note, setNote] = useState("");
  const [error, setError] = useState<ErrorBody["error"] | null>(null);
  const [markError, setMarkError] = useState<ErrorBody["error"] | null>(null);
  const [drawer, setDrawer] = useState(false);
  const [pending, setPending] = useState(false);
  const [running, setRunning] = useState(false);
  const { go: advance } = useSoloAdvance(runId, soloAdvance);
  const [ackedId, setAckedId] = useState<string | null>(null);
  // The note saved with the acknowledgement comes back on `croMemo.ackNote`, so a reload shows it; just after acknowledging it is what was typed.
  const [typedAtAck, setTypedAtAck] = useState("");
  const queryClient = useQueryClient();
  const kept = memo?.ackNote ?? typedAtAck;

  // A memo built over a plan that moved on is rebuilt, quietly: the stale answer is not an error the player acts on, so it is retried a few times (the plan is still being saved) and only a failure that persists is shown.
  // The route call is a new function on every render of the parent, so it is read through a ref: the build depends on the plan it describes (run, round, draft version) and nothing else, and one ask is in flight at a time.
  const ask = useRef(soloCroMemo);
  ask.current = soloCroMemo;
  const flying = useRef<string | null>(null);
  const roundId = round?.id ?? null;
  const build = useCallback(async () => {
    if (roundId === null || draftVersion === null) return;
    const key = `${runId}:${roundId}:${draftVersion}`;
    if (flying.current === key) return;
    flying.current = key;
    let faults = 0;
    try {
      for (let attempt = 0; ; attempt++) {
        const r = await ask.current({ runId, roundId }, { draftVersion });
        if (flying.current !== key) return; // the plan moved on while this ask was out
        if (r.ok) { setMemo(r.value); setError(null); return; }
        const stale = r.error.code === "CRO_MEMO_STALE";
        if (stale ? attempt >= STALE_RETRIES : r.error.code !== "INTERNAL" || faults++ >= FAULT_RETRIES) { setError(r.error); return; }
        await new Promise((resolve) => setTimeout(resolve, STALE_RETRY_MS));
        if (flying.current !== key) return;
      }
    } finally { if (flying.current === key) flying.current = null; }
  }, [roundId, draftVersion, runId]);

  useEffect(() => { if (memo === null) void build(); }, [memo, build]);
  useEffect(() => { if (memo !== null) onMemo?.(memo); }, [memo, onMemo]);

  // The read mark lives on the server: the note's content on screen is what marks it (once per note content), so a reload keeps it and a rebuilt note is read again.
  // A stale answer marks nothing: the note is asked for again and marked when the rebuilt content is shown.
  const mark = useRef(soloOpenCroMemo);
  mark.current = soloOpenCroMemo;
  const tried = useRef<Set<string>>(new Set());
  const readHash = useRef<string | null>(null);
  const teamId = team.team.id;
  useEffect(() => {
    if (!drawer || memo === null || roundId === null || memo.openedAt != null || memo.acknowledgedAt !== null) return;
    const hash = memo.memo.memoHash;
    if (tried.current.has(hash)) return;
    tried.current.add(hash);
    void (async () => {
      const r = await mark.current({ runId, roundId, memoId: memo.memoId }, { memoHash: hash });
      if (r.ok) {
        readHash.current = hash;
        setMarkError(null);
        setMemo((cur) => (sameNote(cur, r.value) ? r.value : cur));
        queryClient.setQueriesData({ queryKey: ["teamState", { teamId }] }, (old: unknown) => (typeof old === "object" && old !== null && "croMemo" in old && sameNote((old as { croMemo: Memo | null }).croMemo, r.value) ? { ...old, croMemo: r.value } : old));
      } else if (r.error.code === "CRO_MEMO_STALE") {
        setMemo((cur) => (cur !== null && cur.memo.memoHash === hash ? null : cur));
      } else { tried.current.delete(hash); setMarkError(r.error); }
    })();
  }, [drawer, memo, roundId, runId, teamId, queryClient]);

  if (round === null || draftVersion === null) return <p className="gx-muted" data-testid="solo.review.none">There is no plan to review yet.</p>;
  if (round.soloPhase === "awaiting_event") {
    return <GxCard title="An event is waiting" testId="solo.review.event"><div className="gx-card-b"><p className="gx-narr">Your year is paused for an event.</p><button type="button" className="gx-btn gx-btn--primary" onClick={() => navigate(`/solo/${runId}`)}>Open the event</button></div></GxCard>;
  }
  if (memo === null && error?.code === "INTERNAL") {
    return (
      <div id="memo"><GxCard title="Your risk officer's memo" testId="solo.memo.unavailable">
        <div className="gx-card-b"><p className="gx-narr">The memo is unavailable right now. Your plan is saved.</p><button type="button" className="gx-btn gx-btn--secondary" onClick={() => { setError(null); void build(); }}>Retry</button></div>
      </GxCard></div>
    );
  }
  if (memo === null && error !== null) return <GxErrorBanner error={error} />;
  if (memo === null) {
    // The memo is being built: the page keeps its final layout (header, plan, dashboard, note card) with quiet placeholders, so it does not grow when the memo arrives.
    return (
      <div className="gx-rv" data-testid="solo.memo.updating" aria-busy="true">
        <ReviewHead eyebrow={eyebrow} totals={totals}>
          {phone ? null : <span className="gx-submit"><button type="button" className="gx-btn gx-btn--secondary gx-btn--lg gx-btn-lock gx-btn--locked" disabled><Icon name="lock" size={16} />Submit and run the year</button></span>}
        </ReviewHead>
        {plan}
        {riskPending}
        <div id="memo"><ReviewPending title="Your risk officer's note" height={96} rows={1} words="Updating the risk officer’s memo…" testId="solo.memo.pending" note={MEMO_PENDING_NOTE} /></div>
        {phone ? <GxStickyBar className="gx-rv-bar" testId="solo.bar" lead={<><b>Getting the note ready</b><span className="gx-small gx-muted">Submit opens once you have read it.</span></>}><button type="button" className="gx-btn gx-btn--secondary gx-btn--lg gx-btn-lock gx-btn--locked" disabled><Icon name="lock" size={16} />Submit and run the year</button></GxStickyBar> : null}
      </div>
    );
  }

  const opened = memo.openedAt != null;
  const changed = !opened && readHash.current !== null && memo.memo.memoHash !== readHash.current;
  const acknowledged = memo.acknowledgedAt !== null || ackedId === memo.memoId;
  // An objection in the memo is signed over only with a reason (the facilitated CEO's override note); it is stored with the submission.
  const objection = memo.memo.items.some((i) => i.severity === "objection");
  const reasonShort = objection && (note.trim().length < OVERRIDE_MIN || note.trim().length > INPUT_LIMITS.soloNote);
  const fail = (e: ErrorBody["error"]) => { if (e.code === "CRO_MEMO_STALE") { setMemo(null); setError(null); } else setError(e); };
  const acknowledge = async (): Promise<boolean> => {
    setPending(true);
    const r = await soloAckCroMemo({ runId, roundId: round.id, memoId: memo.memoId }, { memoHash: memo.memo.memoHash, ...(note.trim() === "" ? {} : { note: note.trim() }) });
    if (r.ok) { setAckedId(memo.memoId); setError(null); setTypedAtAck(note.trim()); } else { setPending(false); fail(r.error); }
    return r.ok;
  };
  const submit = async () => {
    setPending(true);
    setRunning(true);
    const r = await advance({ action: "submit", roundId: round.id, draftVersion, croMemoId: memo.memoId }, () => navigate(`/solo/${runId}`));
    if (!r.ok) { setPending(false); setRunning(false); fail(r.error); }
  };

  /** One primary action: it records the read (once) and runs the year. The tick in the card is what turns it on. */
  const runYear = async () => { if (acknowledged || (await acknowledge())) await submit(); };
  // One action reads the note: opening it marks it read (no tick to add), and that is what unlocks the button.
  const noteOver = !acknowledged && note.trim().length > INPUT_LIMITS.soloNote;
  const ready = objection ? acknowledged || (opened && !reasonShort) : (acknowledged || opened) && !noteOver;
  // The receipt counts the sections the note lists, in the note's own words (the dashboard above keeps its own flagged / to-watch count).
  const receipt = memoReceipt(memo.memo.items, team.draft?.capped);
  const submitLabel = objection ? "Sign over the objection and run the year" : "Submit and run the year";
  const missing = noteOver ? "Shorten your note to submit." : objection && opened ? `Say why first (${OVERRIDE_MIN}+ characters)` : "Read the note to submit.";

  const submitButton = <button type="button" className={ready ? "gx-btn gx-btn--gold gx-btn--lg gx-btn-lock" : "gx-btn gx-btn--secondary gx-btn--lg gx-btn-lock gx-btn--locked"} data-testid="solo.submit" aria-busy={pending || undefined} disabled={pending || !ready} onClick={() => void runYear()}>{ready ? null : <Icon name="lock" size={16} />}{pending ? (running ? (objection ? "Signing over and running the year…" : "Submitting…") : "Recording that you read the note…") : submitLabel}</button>;
  // The things that stand before the submit, from the gate's own conditions: the note read, and for an objection the reason written.
  const before = (acknowledged || opened ? 0 : 1) + ((objection && !acknowledged && reasonShort) || noteOver ? 1 : 0);
  const reading = !ready && !(opened && (objection || noteOver));
  const bar = (
    <GxStickyBar className="gx-rv-bar" testId="solo.bar" lead={ready ? <b>Ready to submit</b> : <><b>{before === 1 ? "1 thing before you submit" : `${before} things before you submit`}</b><span className="gx-small gx-muted" data-testid={reading ? "solo.submit.lead" : "solo.submit.why"}>{missing}</span></>}>
      {reading ? <button type="button" className="gx-btn gx-btn--primary gx-btn--lg" data-testid="solo.submit.why" onClick={() => setDrawer(true)}>Read the note</button> : null}
      {submitButton}
    </GxStickyBar>
  );
  return (
    <div className="gx-rv" data-testid="solo.review">
      <ReviewHead eyebrow={eyebrow} totals={totals}>
        {phone ? null : <span className="gx-submit">
          {ready ? null : opened && (objection || noteOver) ? <span className="gx-small gx-faint gx-submit-why" data-testid="solo.submit.why">{missing}</span>
            : <button type="button" className="gx-btn gx-btn--secondary gx-btn--lg gx-submit-why" data-testid="solo.submit.why" onClick={() => setDrawer(true)}>Read the note</button>}
          {submitButton}
        </span>}
      </ReviewHead>
      {plan}
      {risk === undefined ? null : risk(memo.memo.items)}
      <div id="memo"><GxCard title="Your risk officer's note" aside={<GxPill tone={acknowledged || opened ? "good" : "neutral"} testId="solo.memo.state">{acknowledged || opened ? "\u2713 Read" : "Unread"}</GxPill>} className="gx-rv-card" testId="solo.memo">
        <div className="gx-rv-receipt">
          {changed ? <p className="gx-small gx-muted" role="status" data-testid="solo.memo.changed">{CHANGED_LINE}</p> : null}
          <p data-testid="solo.memo.receipt">{receipt === null ? "Nothing to flag: your plan sits inside your limits." : `${receipt}.`}</p>
          <button type="button" className="gx-btn gx-btn--secondary" data-testid="solo.memo.open" onClick={() => setDrawer(true)}>Read the note</button>
        </div>
      </GxCard></div>
      {drawer ? <Drawer title="Your risk officer's note" onClose={() => setDrawer(false)}><GxCroMemoList items={memo.memo.items} testPrefix="solo.memo" capped={team.draft?.capped} /></Drawer> : null}
      {reasons}
      {error === null && markError === null ? null : <div className="gx-solo-gap"><GxErrorBanner error={(error ?? markError) as ErrorBody["error"]} /></div>}
      {running ? <p className="gx-notice" role="status" data-testid="solo.running">Running your year: twelve months for every bank in the game. Stay on this page; it moves on when the year is done.</p> : null}
      <div className="gx-solo-memo-ack">
        {/* The note box keeps its place after the read: it turns read-only. */}
        <div data-testid={acknowledged && kept !== "" ? "solo.memo.kept" : undefined}>
          <GxLimitedText id="solo-note" fieldTestId="solo.memo.note" label={acknowledged ? "Your note" : objection ? `Sign over the risk officer's objection \u2014 say why (${OVERRIDE_MIN}+ characters)` : "A note for your own record (optional)"} max={INPUT_LIMITS.soloNote} rows={objection ? 2 : 1} value={acknowledged ? kept : note} readOnly={acknowledged} placeholder={acknowledged ? "No note" : undefined} onChange={(t) => setNote(t)} />
        </div>
      </div>
      {phone ? bar : null}
    </div>
  );
};
